import ActionText from '@liveauctioneers/caterwaul-components/lib/ActionText/ActionText';
import Input from '@liveauctioneers/caterwaul-components/lib/Input/Input';
import { safelyRender, sanitizeHeaderText } from '@liveauctioneers/caterwaul-components/lib/utils/safelyRender';
import { CaretRight, PencilSimple } from '@liveauctioneers/hammer-ui-core/icons';
import { Loading } from '@liveauctioneers/hammer-ui-core/loading';
import { BodySecondary } from '@liveauctioneers/hammer-ui-core/text';
import * as React from 'react';
import isEqual from 'react-fast-compare';
import styled, { css } from 'styled-components';

type FolderProps = {
    readonly activeFolder: number | null;
    readonly archive?: boolean;
    readonly count: number;
    readonly edit?: boolean;
    readonly id: number | string;
    readonly loading: boolean;
    readonly name: string;
    readonly onArchiveFolder?: Function;
    readonly onRenameFolder?: Function;
    readonly onSetActiveInbox: Function;
    readonly renaming?: boolean;
    readonly setArchive?: Function;
    readonly setRenaming?: Function;
    readonly testid?: string;
};

const Folder = ({
    activeFolder,
    archive,
    count,
    edit,
    id,
    loading,
    name: existingName,
    onArchiveFolder = () => {},
    onRenameFolder = () => {},
    onSetActiveInbox,
    renaming,
    setArchive = () => {},
    setRenaming = () => {},
    testid = 'messengerFolder',
}: FolderProps) => {
    const active = activeFolder === id;
    const [name, setName] = React.useState(existingName);

    let inner = (
        <div className="flex items-center">
            <StyledInboxButton
                $active={active}
                $editing={edit}
                disabled={loading}
                onClick={() => !loading && onSetActiveInbox(id)}
            >
                <BodySecondary title={sanitizeHeaderText(name)}>
                    {safelyRender({ html: name, wrapper: 'span' })}
                </BodySecondary>
                {!edit &&
                    (loading && active ? <Loading size={24} /> : <BodySecondary>{count ? count : ''}</BodySecondary>)}
            </StyledInboxButton>
            {edit && (
                <div className="flex items-center gap-xs">
                    <StyledArchiveActionText onClick={() => setArchive(true)}>
                        <BodySecondary>Archive</BodySecondary>
                    </StyledArchiveActionText>
                    {' | '}
                    <StyledPencilSimple
                        onClick={() => setRenaming(true)}
                        size="xs"
                    />
                </div>
            )}
        </div>
    );

    // @ts-ignore < should work not work figuring out exact type for now
    const handleFocus = (event: React.FocusEvent<HTMLElement>) => event.target.select();

    const handleKeyPress = (event: React.KeyboardEvent<HTMLElement>) => {
        if (event.key === 'Enter') {
            onRenameFolder(id, name.trim());
            setRenaming(false);
        }
        event.stopPropagation();
    };

    const handleOnChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const target = event.target;
        setName(target.value);
        event.stopPropagation();
    };

    const handleButtonClick = () => {
        if (name.trim()) {
            onRenameFolder(id, name.trim());
            setRenaming(false);
        }
    };

    if (archive) {
        inner = (
            <StyledArchiveContainer>
                <BodySecondary>Archive?</BodySecondary>
                <StyledArchiveControls>
                    <StyledActionText
                        onClick={() => {
                            onArchiveFolder(id);
                            setArchive(false);
                            if (active) {
                                onSetActiveInbox(0);
                            }
                        }}
                    >
                        <BodySecondary>Yes</BodySecondary>
                    </StyledActionText>
                    <BodySecondary>&nbsp;|&nbsp;</BodySecondary>
                    <StyledActionText
                        onClick={() => {
                            setArchive(false);
                            setRenaming(false);
                        }}
                    >
                        <BodySecondary>Cancel</BodySecondary>
                    </StyledActionText>
                </StyledArchiveControls>
            </StyledArchiveContainer>
        );
    } else if (renaming) {
        inner = (
            <StyledRenamingContainer>
                <Input
                    aria-label="New folder name"
                    hideSpacer
                    id="newFolder"
                    label="New folder name"
                    onChange={handleOnChange}
                    onFocus={handleFocus}
                    onKeyPress={handleKeyPress}
                    small
                    value={name}
                />
                <StyledIconContainer onClick={handleButtonClick}>
                    <CaretRight size="xs" />
                </StyledIconContainer>
            </StyledRenamingContainer>
        );
    }

    return (
        <StyledFolder
            $active={active}
            data-testid={testid}
        >
            {inner}
        </StyledFolder>
    );
};
export default React.memo<FolderProps>(Folder, isEqual);

const StyledFolder = styled.li<{ $active: boolean }>`
    ${({ $active, theme: { colors } }) => css`
        list-style: none;
        padding: 0 10px;
        background-color: ${$active ? colors.grey500 : colors.white};

        &:hover {
            background-color: ${colors.grey500};
        }
    `}
`;

const StyledInboxButton = styled(ActionText)<{
    $active: boolean;
    $editing?: boolean;
}>`
    ${({ $active, $editing, theme: { colors } }) => css`
        display: inline-flex;
        width: ${$editing ? 'calc(100% - 82px)' : '100%'};
        justify-content: space-between;
        text-decoration: none;
        padding: 6px 0px;
        background-color: none !important;

        &:hover,
        &:focus {
            text-decoration: none;
        }

        & span {
            color: ${$active ? colors.blue100 : colors.steel};

            &:hover,
            &:focus {
                color: ${colors.blue200};
            }
        }

        & > h6:first-child,
        & > span:first-child {
            width: 170px;
            white-space: nowrap;
            overflow: hidden;
            display: inline-block;
            text-overflow: ellipsis;
            text-align: left;
        }

        & > span:last-child {
            color: ${colors.grey100} !important;
        }
    `}
`;

const StyledArchiveContainer = styled.div`
    width: 204px;
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    background: ${({ theme }) => theme.colors.grey500};
    padding: 0 10px;
    margin: 0 -10px;
`;

const StyledRenamingContainer = styled.div`
    width: 224px;
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    padding: 0 10px;
    margin: 0 -10px;
`;

const StyledIconContainer = styled.div`
    background: ${({ theme }) => theme.colors.blue100};
    margin-top: 21px;
    min-width: 34px;
    height: 34px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;

    &:hover {
        background: ${({ theme }) => theme.colors.blue200};
    }

    svg {
        color: ${({ theme }) => theme.colors.white};
    }
`;

const StyledPencilSimple = styled(PencilSimple)`
    width: 14px;
    height: 14px;
    color: ${({ theme }) => theme.colors.blue100};
    cursor: pointer;

    &:hover {
        color: ${({ theme }) => theme.colors.blue200};
    }
`;

const StyledArchiveControls = styled.div`
    flex: 0 1 auto;
    display: inline-flex;
    justify-content: space-between;
    align-items: center;
    max-height: 34px;
`;

const StyledArchiveActionText = styled(ActionText)`
    display: inline-flex;
    padding: 6px 0;
    max-height: 34px;

    span {
        color: ${({ theme }) => theme.colors.blue100};
        &:hover,
        &:focus {
            color: ${({ theme }) => theme.colors.blue200};
            text-decoration: none;
            background: ${({ theme }) => theme.colors.grey500} !important;
        }
    }
`;

const StyledActionText = styled(ActionText)`
    padding: 6px 0 !important;
    width: auto !important;
    background: none !important;

    span {
        color: ${({ theme }) => theme.colors.blue100};
        &:hover {
            color: ${({ theme }) => theme.colors.blue200};
        }
    }
`;
